import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  CheckIcon,
  CloseIcon,
  // EyeIcon,
  // EyeOffIcon,
  FloppyDiscIcon,
  MaximizeIcon,
  MinimizeIcon,
  UploadIcon,
} from '@iconicicons/react';
import {
  useCreateContractGroupMutation,
  useGetContractGroupsQuery,
  useLazyGetContractsQuery,
} from '../../services/edgeApi';
import { toIsoDate } from '../../utils/stringUtils';
import { DateSelector } from '../../helpers/DateSelector';
import {
  scenarioActions,
  selectEditState,
  selectFlowDate,
  selectScenarioContracts,
} from '../../scenarios/scenarioSlice';
import {
  contractActions,
  selectContracts,
} from '../../contracts/contractsSlice';
import { useLocation } from 'react-router-dom';
import { UncontrolledTooltip } from 'reactstrap';
import toast from 'react-hot-toast';
import { Contract } from '@/types';
import {
  ContractTable,
  ContractTableFilters,
} from '../../contracts/ContractTable';
import { ReusableModal } from '../../modals/ReusableModal';
import { ContractSelection } from './ContractSelection';
import { selectAllPoints } from '../../map/slice/mapSelectors';

export const ScenarioContracts: React.FC = () => {
  const componentType = 'contracts';

  const flowDate = useSelector(selectFlowDate);
  const stateContracts = useSelector(selectScenarioContracts);
  const isEditing = useSelector(selectEditState);
  const visibleContracts = useSelector(selectContracts);
  const points = useSelector(selectAllPoints);

  const [fullscreen, setFullscreen] = useState<boolean>(false);
  const [activeContractsDate, setActiveContractsDate] = useState(
    new Date(flowDate),
  );
  const [filters, setFilters] = useState<ContractTableFilters | undefined>();

  const [contractsTrigger, { isFetching: fetchingActiveContracts }] =
    useLazyGetContractsQuery();
  const { data: contractGroups } = useGetContractGroupsQuery(undefined);
  const [createContractGroupTrigger] = useCreateContractGroupMutation();
  const dispatch = useDispatch();

  const location = useLocation();

  const [selectedContractGroup, setSelectedContractGroup] =
    useState<number>(-1);
  const [creatingNewPreset, setCreatingNewPreset] = useState<boolean>(false);
  const [newPresetName, setNewPresetName] = useState<string>('');
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [isAddingContracts, setIsAddingContracts] = useState<boolean>(false);
  // const [bulkToggle, setBulkToggle] = useState<boolean>(false);

  // const tableRef = useRef<EditableTableRef<Contract> | null>(null);
  const presetNameInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setActiveContractsDate(new Date(flowDate));
  }, [flowDate]);

  const loadContracts = async () => {
    const result = await contractsTrigger({
      date: undefined,
      include_details: true,
    }).unwrap();
    setContracts(Object.values(result));
  };

  useEffect(() => {
    loadContracts();
  }, []);

  useEffect(() => {
    dispatch(contractActions.resetState());
    return () => {
      dispatch(contractActions.resetState());
    };
  }, [location.pathname]);

  const overwriteWithActiveContracts = async () => {
    try {
      const result = await contractsTrigger({
        date: toIsoDate(activeContractsDate),
        include_details: true,
      }).unwrap();
      bulkChangeContracts(result, true);
    } catch (e) {
      console.error(e);
    }
  };

  const onChangeContract = (contract: Contract, checked: boolean) => {
    if (checked) {
      dispatch(
        scenarioActions.updateComponent({
          componentType,
          component: { ...contract, id: contract.id },
        }),
      );
    } else {
      dispatch(
        scenarioActions.removeComponent({
          componentType,
          componentId: contract.id.toString(),
          saveSetting: false,
        }),
      );
    }
  };

  const onChangeContractVisibility = (
    contracts: Contract[],
    visible: boolean,
  ) => {
    if (visible) {
      contracts.map((contract) => {
        dispatch(contractActions.addContract(contract));
      });
    } else {
      contracts.map((contract) => {
        dispatch(contractActions.removeContract(contract.id));
      });
    }
  };

  const bulkChangeContracts = (contracts: Contract[], checked: boolean) => {
    const otherContracts = stateContracts.filter((contract) =>
      contracts.every((c) => c.id !== contract.id),
    );
    if (checked) {
      dispatch(
        scenarioActions.overwrite({
          componentType,
          components: [...otherContracts, ...contracts],
        }),
      );
    } else {
      dispatch(
        scenarioActions.overwrite({
          componentType,
          components: otherContracts,
        }),
      );
    }
  };

  const applyContractFromPreset = async () => {
    if (
      !selectedContractGroup ||
      !contractGroups ||
      !contractGroups[selectedContractGroup]
    ) {
      return;
    }
    const group = contractGroups[selectedContractGroup];
    dispatch(
      scenarioActions.overwrite({
        componentType,
        components: group.contracts
          .map((contractId) => contracts.find((c) => c.id === contractId))
          .filter((c): c is Contract => c !== undefined),
      }),
    );
  };

  const enterCreatePresetMode = () => {
    setCreatingNewPreset(true);
  };

  const saveNewPreset = async () => {
    const contractIds = stateContracts.map((contract) => contract.id);
    try {
      const result = await createContractGroupTrigger({
        name: newPresetName,
        contracts: contractIds,
      }).unwrap();
      setNewPresetName('');
      setCreatingNewPreset(false);
      setSelectedContractGroup(result.id);
    } catch {
      toast.error('Failed to save preset');
    }
  };

  useEffect(() => {
    if (creatingNewPreset) {
      presetNameInputRef?.current?.focus();
    }
  }, [creatingNewPreset]);

  const includedContractIds: Record<string | number, boolean> = useMemo(
    () =>
      stateContracts.reduce(
        (acc, contract) => ({ ...acc, [contract.id]: true }),
        {},
      ),
    [stateContracts],
  );

  const includedContracts = useMemo(
    () =>
      contracts.filter((contract) => includedContractIds[contract.id] === true),
    [contracts, includedContractIds],
  );

  const filteredContracts = useMemo(() => {
    if (!filters) return includedContracts;
    return includedContracts.filter((contract) => {
      const data: Contract = { ...contract };
      return Object.entries(filters)
        .filter(([_key, value]) => {
          if (value === null || value === undefined) {
            return true;
          }
          if (typeof value === 'string') {
            return value.trim().length > 0;
          }
          return true;
        })
        .map(([key, value]) => {
          return [key, String(value).trim().toLowerCase()] as [
            keyof Contract,
            string,
          ];
        })
        .every(([key, value]) => {
          switch (key) {
            case 'owner':
              const ownerValue = data.organization_name ?? data.owner;
              return String(ownerValue).trim().toLowerCase().includes(value);
            case 'receipt_points':
              return data.receipt_points
                .map((pointId) => points[pointId] ?? {})
                .some(
                  (point) =>
                    point.name?.trim().toLowerCase().includes(value) ||
                    point.receipt_zone_name
                      ?.trim()
                      .toLowerCase()
                      .includes(value),
                );
            case 'delivery_points':
              return data.delivery_points
                .map((pointId) => points[pointId] ?? {})
                .some(
                  (point) =>
                    point.name?.trim().toLowerCase().includes(value) ||
                    point.delivery_zone_name
                      ?.trim()
                      .toLowerCase()
                      .includes(value),
                );
            case 'max_daily_quantity':
              const [min, max] = filters.max_daily_quantity;
              if (min !== undefined && data.max_daily_quantity < min) {
                return false;
              }
              if (max !== undefined && data.max_daily_quantity > max) {
                return false;
              }
              return true;
            default:
              return String(data[key]).trim().toLowerCase().includes(value);
          }
        });
    });
  }, [includedContracts, filters]);

  return (
    <>
      {isEditing === true && (
        <>
          <div className="row justify-content-start m-3">
            <div className="col-3 col-md-4 col-sm-6">
              <DateSelector
                value={toIsoDate(activeContractsDate)}
                onChange={(evt) =>
                  setActiveContractsDate(evt.target.valueAsDate ?? new Date())
                }
                isLoading={fetchingActiveContracts}
                onConfirm={overwriteWithActiveContracts}
                buttonText="Add Active Contracts"
              />
            </div>
            <div className={creatingNewPreset ? 'col' : 'd-none'}>
              <div
                className="input-group"
                style={{
                  maxWidth: '100%',
                }}
              >
                <input
                  type="text"
                  className="form-control"
                  placeholder="Group name"
                  ref={presetNameInputRef}
                  value={newPresetName}
                  onChange={(evt) => setNewPresetName(evt.target.value)}
                />
                <button
                  className="btn btn-outline-primary"
                  onClick={saveNewPreset}
                >
                  <CheckIcon />
                </button>
                <button
                  className="btn btn-outline-danger"
                  onClick={() => setCreatingNewPreset(false)}
                >
                  <CloseIcon />
                </button>
              </div>
            </div>
            {!creatingNewPreset && (
              <div className="col-auto ms-auto">
                <div
                  className="input-group"
                >
                  <select
                    className="form-select"
                    value={selectedContractGroup}
                    onChange={(evt) =>
                      setSelectedContractGroup(Number(evt.target.value))
                    }
                  >
                    <option disabled value={-1}>
                      Select Contract Group
                    </option>
                    {Object.values(contractGroups ?? {}).map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn btn-primary"
                    id="apply-contract-preset-button"
                    onClick={applyContractFromPreset}
                  >
                    <UploadIcon />
                  </button>
                  <UncontrolledTooltip target="apply-contract-preset-button">
                    Load contracts from preset
                  </UncontrolledTooltip>
                  <button
                    className="btn btn-primary"
                    id="save-contracts-button"
                    onClick={enterCreatePresetMode}
                  >
                    <FloppyDiscIcon />
                  </button>
                  <UncontrolledTooltip target="save-contracts-button">
                    Save new preset
                  </UncontrolledTooltip>
                </div>
              </div>
            )}
          </div>
          <div className="row justify-content-between m-3">
            <div className="col-3 col-md-4 col-sm-6 d-flex">
              <button
                className="btn btn-primary"
                onClick={() => setIsAddingContracts(true)}
              >
                Change Contract Selection
              </button>
            </div>
          </div>
        </>
      )}
      <ContractTable
        contracts={filteredContracts}
        visibleContracts={visibleContracts}
        setVisibility={onChangeContractVisibility}
        isLoading={fetchingActiveContracts}
        onChangeFilters={setFilters}
        isEditable={isEditing}
        editIcon={CloseIcon}
        onEditContract={(c) => onChangeContract(c, false)}
        showUseInScenarios={false}
      />
      <ReusableModal
        style={fullscreen ? {} : { maxWidth: '2000px' }}
        isOpen={isAddingContracts}
        fullscreen={fullscreen}
        onClose={() => setIsAddingContracts(false)}
      >
        <div>
          <h3 className="text-center mb-2">
            Available Contracts
            <span className="float-end">
              {fullscreen ? (
                <MinimizeIcon
                  role="button"
                  onClick={() => setFullscreen(false)}
                />
              ) : (
                <MaximizeIcon
                  role="button"
                  onClick={() => setFullscreen(true)}
                />
              )}
              <CloseIcon
                role="button"
                onClick={() => setIsAddingContracts(false)}
              />
            </span>
          </h3>
        </div>
        <ContractSelection
          selectedContracts={includedContracts}
          onChange={onChangeContract}
          onBulkChange={bulkChangeContracts}
        />
      </ReusableModal>
    </>
  );
};
